/// <amd-module name="Widgets/Burndown/BurndownWidgetComponents/BurndownDataManager" />
import { Iteration } from "Analytics/Boards/BoardsQueries/IterationsQuery";
import { CompletedWorkItemIdsQuery } from "Analytics/Boards/BurndownQueries/CompletedWorkItemsQuery";
import { CurrentEffortQuery } from "Analytics/Boards/BurndownQueries/CurrentEffortQuery";
import { EffortSnapshotsQuery } from "Analytics/Boards/BurndownQueries/EffortSnapshotsQuery";
import { ProjectsIterationsByDateQuery } from "Analytics/Boards/BurndownQueries/ProjectsIterationsByDateQuery";
import { ProjectsIterationsByIdQuery } from "Analytics/Boards/BurndownQueries/ProjectsIterationsByIdQuery";
import { UnestimatedWorkItemIdsQuery, UnestimatedWorkItemsCountQuery } from "Analytics/Boards/BurndownQueries/UnestimatedWorkItemsQueries";
import { WorkItemIdsSnapshotQuery } from "Analytics/Boards/BurndownQueries/WorkItemIdsSnapshotQuery";
import { AnalyticsExceptionType, extractODataError, recognizeAnalyticsException } from "Analytics/Common/Utilities/AnalyticsExceptionUtilities";
import { DateSamplingSeries } from "Analytics/Common/Utilities/QueryUtilities";
import PingServiceQuery from "Analytics/Queries/PingServiceQuery";
import { ICacheableQueryService, IDashboardsCacheableQueryServiceName } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { onWidgetFailure } from "Dashboards/WidgetCommon/WidgetTelemetry";
import { WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { DemandType } from "Reporting/Visuals/LayoutComponents/Widgets/Demands/DemandType";
import { LayoutState, MessageType } from "Reporting/Visuals/LayoutComponents/Widgets/LayoutState";
import { ScalarComponentProps } from "Reporting/Visuals/LayoutComponents/Widgets/ScalarComponent";
import { getProject } from "Tfs/Widget/Context";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { fetchTodayInAccountTimeZone, shiftToUtc } from "WidgetCommon/DateUtils/DateUtils";
import { AggregationMode } from "WidgetCommon/ModernWidgetTypes/CommonConfigurationTypes";
import { WidgetDataManagerBase, WidgetDataManagerOptions } from "WidgetCommon/ModernWidgetTypes/WidgetDataManagerBase";
import { CoreFieldRefNames } from "WidgetCommon/WitUtils/Constants";
import { WiqlURLBuilder } from "WidgetCommon/WitUtils/WiqlUrlBuilder";
import * as WidgetResources from "Widgets/Burndown/Resources";
import { AsyncDataHelpers } from "./AsyncDataHelpers";
import { BurnDirection } from "./BurnDirection";
import { BurndownChartOptionFactory } from "./BurndownChartOptionFactory";
import { BurndownChartClickInfo } from "./BurndownChartOptionFactory";
import { CurrentWorkItemAggregateEffort, WorkItemEffort, WorkItemFieldDescriptor } from "./BurndownDataContract";
import { WorkItemTypeFilterMode } from "./BurndownDataContract";
import { BurndownDataFactory } from "./BurndownDataFactory";
import { BurndownSettings, DateSampleMode, DateSamplingConfiguration, TimePeriodConfiguration } from "./BurndownSettings";
import BurndownSettingsManager from "./BurndownSettingsManager";
import { TimeIntervalUtils } from "./TimeIntervalUtils";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { BurndownConstants } from "./BurndownConstants"

/**
 * Responsible for heuristically optimizing for the lowest cost set of query implementations for Burndown data requirements.
 */
export class BurndownDataManager extends WidgetDataManagerBase {
    private teamContext: WidgetTeamContext;
    private pageContext: IVssPageContext;
    protected dataService: ICacheableQueryService;
    protected settingsManager: BurndownSettingsManager;
    protected isAutomationScenario: boolean;
    
    constructor(
        pageContext: IVssPageContext,
        teamContext: WidgetTeamContext,
        options: WidgetDataManagerOptions,
        protected allowChartClicks: boolean,
        protected command: string = "Burndown",
        settingsManager?: BurndownSettingsManager,
        isAutomationScenario?: boolean
    ) {
        super(pageContext, options);
        this.dataService = pageContext.getService(IDashboardsCacheableQueryServiceName);
        this.settingsManager = settingsManager ? settingsManager : new BurndownSettingsManager(pageContext, teamContext);
        this.isAutomationScenario = isAutomationScenario === true;
        this.teamContext = teamContext;
        this.pageContext = pageContext;
    }

    /** Start running any demands which require async behavior. */
    public async getData(): Promise<LayoutState> {
        let layoutStatePromise;
        if (!(await this.settingsManager.hasSatisfactoryConfiguration(this.widgetPropsForTelemetry))) {
            layoutStatePromise = this.configNotSatisfactory();
        } else if (this.demandsArePresent()) {
            layoutStatePromise = this.getDataImpl();
        } else {
            layoutStatePromise = Promise.resolve(this.currentState);
        }

        return layoutStatePromise;
    }

    public demandsArePresent(): boolean {
        return (
            this.demandTracker.isDemandPresent(DemandType.subtitle) ||
            this.demandTracker.isDemandPresent(DemandType.scalar) ||
            this.demandTracker.isDemandPresent(DemandType.chart) ||
            this.demandTracker.isDemandPresent(DemandType.submetrics)
        );
    }

    /**
     * Gets burn direction
     */
    protected getBurnDirection(): BurnDirection {
        return BurnDirection.Down;
    }

    protected getIterationsPromise(
        timePeriodConfiguration: TimePeriodConfiguration,
        projectIds: string[],
        dataQualityWarnings?: Set<string>
    ): Promise<Iteration[]> {
        const iterationIDs: string[] = timePeriodConfiguration.samplingConfiguration.settings as string[];

        // To avoid running the iterations query with invalid data, we return a helpful error.
        for (const id of iterationIDs) {
            if (!id) {
                return Promise.reject(WidgetResources.Config_InvalidQueryValues);
            }
        }

        const iterationsQuery =
            DateSampleMode[timePeriodConfiguration.samplingConfiguration.identifier] == DateSampleMode[DateSampleMode.ByIterations]
                ? new ProjectsIterationsByIdQuery(this.context, projectIds, iterationIDs, this.command)
                : new ProjectsIterationsByDateQuery(this.context, projectIds, timePeriodConfiguration.startDate, this.command);
        iterationsQuery.setAutomationMode(this.isAutomationScenario);

        const iterationsPromise = this.dataService.getCacheableQueryResult(iterationsQuery);
        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(iterationsQuery), dataQualityWarnings);

        return iterationsPromise;
    }

    protected async getEffortSnapshotsPromise(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        sampleDates: DateSamplingSeries,
        dataQualityWarnings?: Set<string>
    ): Promise<WorkItemEffort[]> {
        const pastEffortQuery = new EffortSnapshotsQuery(
            this.context,
            sampleDates,
            burndownSettings.teams,
            burndownSettings.fieldFilters,
            burndownSettings.aggregation,
            workItemTypes,
            workItemTypesFields,
            burndownSettings.showResolvedItemsAsCompletedEnabled,
            this.command
        );
        pastEffortQuery.setAutomationMode(this.isAutomationScenario);
        const effortSnapshotsPromise = this.dataService.getCacheableQueryResult(pastEffortQuery);
        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(pastEffortQuery), dataQualityWarnings);

        return effortSnapshotsPromise;
    }

    protected getUnestimatedCountPromise(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypeFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): Promise<number> {
        const unestimatedCountQuery = new UnestimatedWorkItemsCountQuery(
            this.context,
            burndownSettings.teams,
            burndownSettings.fieldFilters,
            workItemTypes,
            workItemTypeFields,
            burndownSettings.aggregation.settings,
            burndownSettings.showResolvedItemsAsCompletedEnabled,
            this.command
        );
        unestimatedCountQuery.setAutomationMode(this.isAutomationScenario);
        const unestimatedCountPromise = this.dataService.getCacheableQueryResult(unestimatedCountQuery);
        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(unestimatedCountQuery), dataQualityWarnings);

        return unestimatedCountPromise;
    }

    protected getOnClickChartHandler(
        burndownSettings: BurndownSettings,
        allShownWorkItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): (event: BurndownChartClickInfo) => void {
        return (event: BurndownChartClickInfo) => {
            let workItemTypes: string[];
            // Check if user clicked a series for a particular work item type
            if (
                event.seriesName !== WidgetResources.BurndownWidget_CompletedEffortSeriesName &&
                event.seriesName !== WidgetResources.BurndownWidget_RemainingEffortSeriesName
            ) {
                // Query for single item type if user clicked on specific item type -
                // i.e.not green or blue "Completed" or "Remaining"
                workItemTypes = [event.seriesName];
            } else {
                // Otherwise query all types in that category
                workItemTypes = allShownWorkItemTypes;
            }

            // Determine which items to query - completed or remaining

            // For burndown, we query completed only if user explicitly clicked green "Completed"
            // which can't have specific work item series.
            // Specific work item types only show up as remaining.

            // For burnup, we always query completed items, as we don't show remaining ones.

            const queryCompletedItems =
                this.getBurnDirection() === BurnDirection.Down ? event.seriesName === WidgetResources.BurndownWidget_CompletedEffortSeriesName : true;

            // Some browser popup blockers do not allow opening a new window if it is not performed on the main thread
            // of execution resulting from a user action. To circumvent this we open a window on the main thread and
            // assign a new URL to it later once we've constructed it.
            const tempWindow = window.open();
            if (tempWindow) {
                tempWindow.opener = null;
            }

            const workItemIdsSnapshotQuery = new WorkItemIdsSnapshotQuery(
                this.context,
                event.sampleDate,
                burndownSettings.teams,
                burndownSettings.fieldFilters,
                workItemTypesFields,
                burndownSettings.aggregation,
                queryCompletedItems,
                workItemTypes,
                burndownSettings.showResolvedItemsAsCompletedEnabled,
                this.command
            );
            // By definition, this path is driven by user interaction.

            this.dataService
                .getCacheableQueryResult(workItemIdsSnapshotQuery)
                .then(result => {
                    const workItemIds: string[] = result;
                    const queryNamePrefix = queryCompletedItems
                        ? WidgetResources.BurndownWidget_CompletedEffortSeriesName
                        : WidgetResources.BurndownWidget_RemainingEffortSeriesName;
                    const tempQueryName = `${queryNamePrefix} work items - ${event.dataPointName}`;

                    this.navigateToWorkItemList(tempQueryName, workItemIds, tempWindow);
                })
                .then(undefined, e => {
                    const errorMessage = extractODataError(e);
                    onWidgetFailure(this.context, this.widgetPropsForTelemetry, errorMessage, "Handle chart click", {
                        ErrorDetail: JSON.stringify(e)
                    });
                });
            this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(workItemIdsSnapshotQuery), dataQualityWarnings);
        };
    }

    protected getCurrentEffortPromise(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypeFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): Promise<CurrentWorkItemAggregateEffort[]> {
        const remainingEffortQuery = new CurrentEffortQuery(
            this.context,
            burndownSettings.teams,
            burndownSettings.fieldFilters,
            burndownSettings.aggregation,
            workItemTypes,
            workItemTypeFields,
            burndownSettings.showResolvedItemsAsCompletedEnabled,
            this.command
        );
        remainingEffortQuery.setAutomationMode(this.isAutomationScenario);

        const currentEffortPromise = this.dataService.getCacheableQueryResult(remainingEffortQuery);
        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(remainingEffortQuery), dataQualityWarnings);

        return currentEffortPromise;
    }

    protected getWitColorDictionaryPromise(
        pageContext: IVssPageContext,
        stackByWorkItemTypeEnabled: boolean,
        workItemTypes: string[]
    ): Promise<{ [key: string]: string } | undefined> {
        let witColorDictionaryPromise;
        if (stackByWorkItemTypeEnabled) {
            // Use current context project to define work item type colors
            const contextProjectName = getProject(pageContext).name;
            try {
                witColorDictionaryPromise = AsyncDataHelpers.getWitColorDictionary(pageContext, workItemTypes, contextProjectName);
            } catch (e) {
                const errorMessage = extractODataError(e);
                onWidgetFailure(pageContext, this.widgetPropsForTelemetry, errorMessage, "Retrieve work item color information", {
                    ErrorDetail: JSON.stringify(e)
                });
                throw e;
            }
        } else {
            witColorDictionaryPromise = Promise.resolve(undefined);
        }
        return witColorDictionaryPromise;
    }

    protected async getDataImpl(): Promise<LayoutState> {
        if (this.settings == undefined) {
            throw new Error(WidgetResources.BurndownWidgetDataManager_NoWidgetSettingsFound);
        }
        const burndownSettings = JSON.parse(this.settings) as BurndownSettings;
        
        // if feature is disabled, always reset value to false
        if(!isFeatureFlagEnabled(this.pageContext,BurndownConstants.featureFlagName_ShowResolvedItemsAsCompleted, false)){
            burndownSettings.showResolvedItemsAsCompletedEnabled = false;
        }

        if (burndownSettings.teams == undefined || burndownSettings.teams.length === 0) {
            throw new Error(WidgetResources.ConfigError_NoTeamsFound);
        }

        const projectIds = burndownSettings.teams.map(team => team.projectId);

        const dataQualityWarnings: Set<string> = new Set<string>();

        // Retrieve the work item types and fields
        const workItemTypesPromise = AsyncDataHelpers.getWorkItemTypes(
            this.context,
            burndownSettings.workItemTypeFilter,
            !!burndownSettings.includeBugsForRequirementCategory,
            this.isAutomationScenario
        );
        const workItemTypesFieldsPromise = workItemTypesPromise.then(workItemTypes =>
            AsyncDataHelpers.getWorkItemTypeFieldsPromise(this.context, workItemTypes, projectIds, this.dataService, this.isAutomationScenario)
        );

        // Retrieve iterations
        let iterationsPromise;
        if (burndownSettings.timePeriodConfiguration.samplingConfiguration.identifier === DateSampleMode.ByIterations) {
            iterationsPromise = this.getIterationsPromise(burndownSettings.timePeriodConfiguration, projectIds, dataQualityWarnings);
        }

        return Promise.all<any>([workItemTypesPromise, workItemTypesFieldsPromise, iterationsPromise])
            .then(([workItemTypes, workItemTypesFields, iterations]) => {
                let chartAndSubmetricUpdatedPromise;
                if (this.demandTracker.isDemandPresent(DemandType.chart) || this.demandTracker.isDemandPresent(DemandType.submetrics)) {
                    const sampleDatesStructure =
                        iterations != null
                            ? TimeIntervalUtils.getSampleDates(burndownSettings.timePeriodConfiguration.startDate, iterations)
                            : TimeIntervalUtils.getSampleDates(burndownSettings.timePeriodConfiguration.startDate, burndownSettings
                                  .timePeriodConfiguration.samplingConfiguration.settings as DateSamplingConfiguration);

                    const effortSnapshotsPromise = this.getEffortSnapshotsPromise(
                        burndownSettings,
                        workItemTypes,
                        workItemTypesFields,
                        sampleDatesStructure,
                        dataQualityWarnings
                    );
                    const currentEffortPromise = this.getCurrentEffortPromise(
                        burndownSettings,
                        workItemTypes,
                        workItemTypesFields,
                        dataQualityWarnings
                    );

                    let unestimatedCountQuery;
                    if (burndownSettings.aggregation.identifier === AggregationMode.Sum) {
                        unestimatedCountQuery = this.getUnestimatedCountPromise(
                            burndownSettings,
                            workItemTypes,
                            workItemTypesFields,
                            dataQualityWarnings
                        );
                    }

                    const witColorDictionaryPromise = this.getWitColorDictionaryPromise(
                        this.context,
                        burndownSettings.stackByWorkItemTypeEnabled,
                        workItemTypes
                    );
                    const todayInAccountTimeZonePromise = fetchTodayInAccountTimeZone(this.context);
                    // Pass date interval if we're not using iterations
                    const sampleDatesInterval =
                        iterations != null
                            ? undefined
                            : (burndownSettings.timePeriodConfiguration.samplingConfiguration.settings as DateSamplingConfiguration).sampleInterval;

                    chartAndSubmetricUpdatedPromise = Promise.all<any>([
                        effortSnapshotsPromise,
                        currentEffortPromise,
                        unestimatedCountQuery,
                        witColorDictionaryPromise,
                        todayInAccountTimeZonePromise
                    ]).then(async ([effortSnapshots, currentEffort, unestimatedCount, witColorDictionary, todayInAccountTimeZone]) => {
                        // Empty chart experience when we have no data. If we have unestimated count data, proceed as normal since
                        // we want to show you that useful submetric.
                        if (effortSnapshots.length === 0 && unestimatedCount == null) {
                            return this.packMessageAsState(MessageType.NoData);
                        } else {
                            // Ensure any prior messaging is cleared out.
                            this.resetMessageState();
                        }

                        const pastDays = sampleDatesStructure.sampleDates.filter(date => localeIgnoreCaseComparer(date, todayInAccountTimeZone) <= 0);

                        const remainingSnapshotEffort = BurndownDataFactory.getRemainingEffort(effortSnapshots, burndownSettings.showResolvedItemsAsCompletedEnabled);
                        const completedSnapshotEffort = BurndownDataFactory.getCompletedEffort(effortSnapshots, burndownSettings.showResolvedItemsAsCompletedEnabled);
                        const datesToTotalSnapshotEffort = BurndownDataFactory.getEffortByDate(effortSnapshots);

                        const datesToEffortForMainSeries = this.resolvePastDays(
                            pastDays,
                            remainingSnapshotEffort,
                            completedSnapshotEffort,
                            datesToTotalSnapshotEffort
                        );

                        const averageBurn = -BurndownDataFactory.getAverageChange(datesToEffortForMainSeries, pastDays);
                        const completedPercentage = BurndownDataFactory.getCompletedPercentage(currentEffort, burndownSettings.showResolvedItemsAsCompletedEnabled);
                        const scopeChange = BurndownDataFactory.getSumOfAggregatedEffort(currentEffort) - datesToTotalSnapshotEffort[pastDays[0]];
                        const allowChartClicks = this.allowChartClicks;

                        const chartOptions = new BurndownChartOptionFactory(this.context).createChartOptions({
                            todayInAccountTimezone: todayInAccountTimeZone,
                            showOtherEffort: burndownSettings.completedWorkEnabled,
                            showScopeTrendline: burndownSettings.totalScopeTrendlineEnabled,
                            showBurnTrendline: burndownSettings.burndownTrendlineEnabled,
                            iterations: iterations,
                            sampleDatesInterval: sampleDatesInterval,
                            lastGeneratedIntervalSampleDate: sampleDatesStructure.lastIntervalDate,
                            sampleDates: sampleDatesStructure.sampleDates,
                            datesToTotalEffort: datesToTotalSnapshotEffort,
                            remainingEffort: remainingSnapshotEffort,
                            completedEffort: completedSnapshotEffort,
                            startDate: burndownSettings.timePeriodConfiguration.startDate,
                            suppressAnimations: this.suppressAnimations,
                            showStackedWorkItemTypes: burndownSettings.stackByWorkItemTypeEnabled,
                            workItemTypeColorDictionary: witColorDictionary,
                            averageBurn: averageBurn,
                            datesToPrimarySeriesEffort: datesToEffortForMainSeries,
                            onClick: this.getOnClickChartHandler(burndownSettings, workItemTypes, workItemTypesFields, dataQualityWarnings),
                            burnDirection: this.getBurnDirection(),
                            allowChartClicks: allowChartClicks
                        });
                        this.currentState.chartData = {
                            chartOptions: chartOptions
                        };

                        // SubMetrics
                        this.currentState.submetricsData = this.getSubMetrics(
                            burndownSettings,
                            workItemTypes,
                            workItemTypesFields,
                            completedPercentage,
                            allowChartClicks,
                            averageBurn,
                            scopeChange,
                            dataQualityWarnings
                        );

                        // Warnings
                        if (dataQualityWarnings.size > 0) {
                            this.currentState.warningsData = Array.from(dataQualityWarnings);
                        }

                        this.updateHeaderAndMetrics(
                            unestimatedCount,
                            sampleDatesStructure.sampleDates,
                            burndownSettings,
                            workItemTypes,
                            workItemTypesFields,
                            allowChartClicks,
                            dataQualityWarnings
                        );
                        return Promise.resolve();
                    });
                }

                return this.handleScalarAndTitlePromise(
                    chartAndSubmetricUpdatedPromise,
                    burndownSettings,
                    workItemTypes,
                    workItemTypesFields,
                    dataQualityWarnings
                );
            })
            .then(undefined /* Catch pattern */, error => {
                return this.handleError(error);
            });
    }

    protected handleScalarAndTitlePromise(
        chartAndSubmetricUpdatedPromise: any,
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): Promise<LayoutState> {
        let scalarDataUpdatedPromise;
        if (this.demandTracker.isDemandPresent(DemandType.scalar)) {
            scalarDataUpdatedPromise = this.getRemainingEffortForHero(burndownSettings, workItemTypes, workItemTypesFields, dataQualityWarnings).then(
                scalarDataProps => {
                    this.currentState.scalarData = scalarDataProps;
                }
            );
        }
        if (this.demandTracker.isDemandPresent(DemandType.title)) {
            this.currentState.title = {
                text: this.title,
                hideTitle: this.hideTitle
            };
        }
        return Promise.all<any>([chartAndSubmetricUpdatedPromise, scalarDataUpdatedPromise]).then(() => this.currentState);
    }

    protected resolvePastDays(
        pastDays: string[],
        remainingSnapshotEffort: WorkItemEffort[],
        completedSnapshotEffort: WorkItemEffort[],
        datesToTotalSnapshotEffort: { [key: string]: number }
    ): { [key: string]: number } {
        let datesToEffortForMainSeries: { [key: string]: number };
        if (this.getBurnDirection() == BurnDirection.Down) {
            datesToEffortForMainSeries = BurndownDataFactory.getEffortByDate(remainingSnapshotEffort);
        } else {
            datesToEffortForMainSeries = BurndownDataFactory.getEffortByDate(completedSnapshotEffort);
        }

        // Fill empty days that came from backend
        pastDays.forEach(day => {
            if (!datesToEffortForMainSeries[day]) {
                datesToEffortForMainSeries[day] = 0; // Use 0 if no effort is defined for the given date
            }
            if (!datesToTotalSnapshotEffort[day]) {
                datesToTotalSnapshotEffort[day] = 0;
            }
        });

        return datesToEffortForMainSeries;
    }

    protected updateHeaderAndMetrics(
        unestimatedCount: number,
        sampleDates: string[],
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        allowChartClicks: boolean,
        dataQualityWarnings?: Set<string>
    ): void {
        if (unestimatedCount != null) {
            const unestimatedSubmetricData = {
                label: WidgetResources.BurndownWidget_ItemsNotEstimatedMetricName,
                value: unestimatedCount.toString(),
                onClick:
                    unestimatedCount > 0 && allowChartClicks
                        ? this.getOnClickUnestimatedHandler(burndownSettings, workItemTypes, workItemTypesFields, dataQualityWarnings)
                        : undefined
            };

            // Insert at position 3
            this.currentState.submetricsData.items.splice(2, 0, unestimatedSubmetricData);
        }

        const startDateString = shiftToUtc(new Date(burndownSettings.timePeriodConfiguration.startDate)).toLocaleDateString();
        const endDateString = shiftToUtc(new Date(sampleDates[sampleDates.length - 1])).toLocaleDateString();

        this.currentState.subtitle = {
            text: `${startDateString} - ${endDateString}`
        };
    }

    protected handleError(error: any): Promise<LayoutState> {
        const errorMessage = extractODataError(error);
        const messageType = mapExceptionToMessage(recognizeAnalyticsException(error));
        onWidgetFailure(this.context, this.widgetPropsForTelemetry, errorMessage, "Retrieve burndown data", {
            ErrorDetail: JSON.stringify(error.stack)
        });
        return this.packMessageAsState(messageType, errorMessage);
    }

    protected getSubMetrics(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        completedPercentage: number,
        allowChartClicks: boolean,
        averageBurn: number,
        scopeChange: number,
        dataQualityWarnings?: Set<string>
    ): any {
        return {
            items: [
                {
                    label: WidgetResources.BurndownWidget_CompletedMetricName,
                    value: completedPercentage + "%",
                    onClick:
                        completedPercentage > 0 && allowChartClicks
                            ? this.getOnClickCompletedHandler(burndownSettings, workItemTypes, workItemTypesFields, dataQualityWarnings)
                            : undefined
                },
                {
                    label:
                        this.getBurnDirection() == BurnDirection.Down
                            ? WidgetResources.BurndownWidget_AverageBurndownMetricName
                            : WidgetResources.BurndownWidget_AverageBurnupMetricName,
                    value: ((this.getBurnDirection() == BurnDirection.Down ? 1 : -1) * BurndownDataFactory.formatAverageBurn(averageBurn)).toString()
                },
                {
                    label: WidgetResources.BurndownWidget_TotalScopeIncreaseMetricName,
                    value: scopeChange ? roundTo(scopeChange, 2).toLocaleString() : "0" // If there's no scope at all, like in initial case, also return zero
                }
            ]
        };
    }

    protected resetMessageState() {
        this.currentState.showMessage = undefined;
        this.currentState.messageType = undefined;
        this.currentState.message = undefined;
    }

    /**
     * Looks up the work item type field name from a list of queried work item type fields.
     * @param aggregationWorkItemTypeFields
     * @param fieldReferenceName
     */
    private getWorkItemTypeFieldName(aggregationWorkItemTypeFields: WorkItemFieldDescriptor[], fieldReferenceName: string): string {
        let fieldName = "";

        for (const workItemTypeField of aggregationWorkItemTypeFields) {
            if (localeIgnoreCaseComparer(workItemTypeField.FieldReferenceName, fieldReferenceName) === 0) {
                fieldName = workItemTypeField.FieldName;
                break;
            }
        }

        return fieldName;
    }

    private getOnClickCompletedHandler(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): () => void {
        return () => {
            const tempWindow = window.open();

            const completedWorkItemIdsQuery = new CompletedWorkItemIdsQuery(
                this.context,
                burndownSettings.teams,
                burndownSettings.fieldFilters,
                workItemTypes,
                workItemTypesFields,
                burndownSettings.aggregation,
                burndownSettings.showResolvedItemsAsCompletedEnabled,
                this.command
            );
            // By definition, this path is driven by user interaction.

            this.dataService
                .getCacheableQueryResult(completedWorkItemIdsQuery)
                .then(result => {
                    const workItemIds = result;
                    const queryName = `Completed - ${this.title}`;

                    this.navigateToWorkItemList(queryName, workItemIds, tempWindow);
                })
                .then(undefined, e => {
                    const errorMessage = extractODataError(e);
                    onWidgetFailure(this.context, this.widgetPropsForTelemetry, errorMessage, "Handle clicking 'completed items' submetric", {
                        ErrorDetail: JSON.stringify(e)
                    });
                });
            this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(completedWorkItemIdsQuery), dataQualityWarnings);
        };
    }

    private getOnClickUnestimatedHandler(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypesFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): () => void {
        return () => {
            const tempWindow = window.open();

            const unestimatedCountQuery = new UnestimatedWorkItemIdsQuery(
                this.context,
                burndownSettings.teams,
                burndownSettings.fieldFilters,
                workItemTypes,
                workItemTypesFields,
                burndownSettings.aggregation.settings,
                burndownSettings.showResolvedItemsAsCompletedEnabled,
                this.command
            );
            // By definition, this path is driven by user interaction.

            this.dataService
                .getCacheableQueryResult(unestimatedCountQuery)
                .then(result => {
                    const workItemIds = result;
                    const queryName = `Unestimated items - ${this.title}`;

                    this.navigateToWorkItemList(queryName, workItemIds, tempWindow);
                })
                .then(undefined, e => {
                    const errorMessage = extractODataError(e);
                    onWidgetFailure(this.context, this.widgetPropsForTelemetry, errorMessage, "Handle clicking 'unestimated items' submetric", {
                        ErrorDetail: JSON.stringify(e)
                    });
                });
            this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(unestimatedCountQuery), dataQualityWarnings);
        };
    }

    private configNotSatisfactory(): Promise<LayoutState> {
        // Ping Analytics to decide whether to show fault-in or unconfigured
        return this.dataService.getCacheableQueryResult(new PingServiceQuery(this.command).setAutomationMode(this.isAutomationScenario)).then(
            () => {
                return this.packMessageAsState(MessageType.Unconfigured);
            },
            e => {
                const messageType = mapExceptionToMessage(recognizeAnalyticsException(e));
                return this.packMessageAsState(messageType, extractODataError(e));
            }
        );
    }
    private navigateToWorkItemList(queryName: string, workItemIds: string[], tempWindow: Window | null): void {
        let workItemIdsString = "-1,-1"; // Default non matching wiql to return to empty result query
        let wiql = "";

        if (workItemIds.length > 0) {
            workItemIdsString = workItemIds.join(",");
        }
        wiql = `SELECT [${CoreFieldRefNames.Id}],[${CoreFieldRefNames.Title}] FROM WorkItems WHERE [${CoreFieldRefNames.Id}] IN (${workItemIdsString})`;

        this.navigateToQueryPage(wiql, queryName, tempWindow);
    }

    private navigateToQueryPage(wiql: string, queryName: string, tempWindow: Window | null) {
        if (tempWindow) {
            new WiqlURLBuilder(this.context).BuildQueryURL(wiql, queryName, this.teamContext.id).then(url => {
                tempWindow.location.href = url;
            });
        }
    }

    private async getRemainingEffortForHero(
        burndownSettings: BurndownSettings,
        workItemTypes: string[],
        workItemTypeFields: WorkItemFieldDescriptor[],
        dataQualityWarnings?: Set<string>
    ): Promise<ScalarComponentProps> {
        const remainingEffortQuery = new CurrentEffortQuery(
            this.context,
            burndownSettings.teams,
            burndownSettings.fieldFilters,
            burndownSettings.aggregation,
            workItemTypes,
            workItemTypeFields,
            burndownSettings.showResolvedItemsAsCompletedEnabled,
            this.command
        );
        remainingEffortQuery.setAutomationMode(this.isAutomationScenario);

        const valuePromise = this.dataService.getCacheableQueryResult(remainingEffortQuery).then(effort => {
            // Calculate total remaining effort
            let value = 0;
            effort.forEach(e => {
                if (!BurndownDataFactory.isWorkItemCompleted(e.StateCategory, e.State,  burndownSettings.showResolvedItemsAsCompletedEnabled))
                {
                    value += e.AggregatedEffort;
                }
            });

            return value;
        });

        this.addDataQualityWarnings(this.dataService.getDataQualityWarnings(remainingEffortQuery), dataQualityWarnings);

        let measurePromise: Promise<string>;
        if (burndownSettings.aggregation.identifier === AggregationMode.Count) {
            if (burndownSettings.workItemTypeFilter.identifier === WorkItemTypeFilterMode[WorkItemTypeFilterMode.BacklogCategory]) {
                measurePromise = AsyncDataHelpers.getPluralBacklogNameOfBacklogCategory(
                    this.context,
                    getProject(this.context).id, // Using current context project for retrieving the plural backlog name
                    burndownSettings.workItemTypeFilter.settings,
                    this.isAutomationScenario
                );
            } else {
                measurePromise = Promise.resolve(burndownSettings.workItemTypeFilter.settings);
            }
        } else {
            const measure = this.getWorkItemTypeFieldName(workItemTypeFields, burndownSettings.aggregation.settings);

            measurePromise = Promise.resolve(measure);
        }

        const [value, measure] = await Promise.all([valuePromise, measurePromise]);
        const description = WidgetResources.BurndownWidget_HeroDescription;

        return {
            description: description,
            measure: measure,
            value: roundTo(value, 2)
        };
    }

    private addDataQualityWarnings(dataQualityPromise: Promise<string[]>, dataQualityWarnings?: Set<string> | undefined): void {
        if (dataQualityWarnings) {
            dataQualityPromise.then(res => {
                res.forEach(warning => {
                    dataQualityWarnings.add(warning);
                });
            });
        }
    }
}

export class BurnupDataManager extends BurndownDataManager {
    /**
     * Gets burn direction
     */
    protected getBurnDirection(): BurnDirection {
        return BurnDirection.Up;
    }
}

export function roundTo(value: number, decimals: number) {
    // Simple rounding solution. Note, it is vulnerable to floating point inaccuracies where 0.005 becomes 0.0049.
    // NOTE: Widgets should not deal presentational extremes. When we move burndown to new plat, we should transition the metrics components to take numeric params which can be centrally rounded, and caller supplied suffixes
    const roundingAmount = Math.pow(10, decimals);
    return Math.round(value * roundingAmount) / roundingAmount;
}

function mapExceptionToMessage(exceptionType: AnalyticsExceptionType): MessageType {
    let messageType = MessageType.WidgetError;
    if (exceptionType === AnalyticsExceptionType.DataNotReady) {
        messageType = MessageType.AxFaultIn;
    } else if (exceptionType === AnalyticsExceptionType.MissingAnalyticsPermissions) {
        messageType = MessageType.MissingPermissions;
    }
    return messageType;
}
